import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../apiConfig";
import { OFICINAS_POR_MUNICIPIO } from "../catalogos/oficinasPorMunicipio";

export default function InventarioForm({ onSaved }) {
  // =======================
  // TOASTS PREMIUM
  // =======================
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  };

  const cerrarToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // =========================
  // CAMPOS
  // =========================
  const [equipoOtro, setEquipoOtro] = useState("");
  const [marcaOtra, setMarcaOtra] = useState("");

  const [form, setForm] = useState({
    id: "",
    equipo: "",
    marca: "",
    modelo: "",
    serie: "",
    estadoEquipo: "",
    fechaAdquisicion: "",
    proyecto: "",
    municipio: "",
    oficina: "",
    modulo: "",
    notas: "",
  });

  const [nuevoID, setNuevoID] = useState("");
  const [oficinasDisponibles, setOficinasDisponibles] = useState([]);
  const [serieDuplicada, setSerieDuplicada] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // =========================
  // GENERAR ID AUTOMÁTICO
  // =========================
  const generarID = () => {
    const id = Math.floor(Math.random() * 900000) + 100000;
    setNuevoID(id);
    setForm((prev) => ({ ...prev, id }));
  };

  useEffect(() => {
    generarID();
  }, []);

  // =========================
  // CAMBIO GENERAL
  // =========================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // =========================
  // VALIDAR SERIE DUPLICADA EN VIVO
  // =========================
  const validarSerieDuplicada = async (serie) => {
    try {
      const res = await axios.get(`${API_BASE}/inventario/buscar?serie=${serie}`);
      return res.data.length > 0;
    } catch (error) {
      console.error("Error validando serie:", error);
      return false;
    }
  };

  useEffect(() => {
    if (!form.serie || form.serie.trim() === "") {
      setSerieDuplicada(false);
      return;
    }

    const check = async () => {
      const serieN = form.serie.trim().toUpperCase();
      const existe = await validarSerieDuplicada(serieN);
      setSerieDuplicada(existe);
    };

    check();
  }, [form.serie]);

  // =========================
  // LIMPIAR FORMULARIO
  // =========================
  const limpiarFormulario = () => {
    setForm({
      id: "",
      equipo: "",
      marca: "",
      modelo: "",
      serie: "",
      estadoEquipo: "",
      fechaAdquisicion: "",
      proyecto: "",
      municipio: "",
      oficina: "",
      modulo: "",
      notas: "",
    });

    setEquipoOtro("");
    setMarcaOtra("");
    setOficinasDisponibles([]);
    setSerieDuplicada(false);
    generarID();
  };

  // =========================
  // HANDLE SUBMIT
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    const obligatorios = [
      "equipo",
      "marca",
      "modelo",
      "estadoEquipo",
      "proyecto",
      "municipio",
      "oficina",
      "modulo",
    ];

    for (let campo of obligatorios) {
      if (!form[campo] || form[campo].trim() === "") {
        showToast(`El campo "${campo}" es obligatorio.`, "warning");
        return;
      }
    }

    if (serieDuplicada) {
      showToast("El número de serie ya está registrado.", "error");
      return;
    }

    setShowConfirm(true);
  };

  // =========================
  // EJECUTAR GUARDADO
  // =========================
  const ejecutarGuardado = async () => {
    setIsSaving(true);

    const dataEnviar = {
      ...form,
      equipo: form.equipo === "Otro" ? equipoOtro : form.equipo,
      marca: form.marca === "OTRA" ? marcaOtra : form.marca,
      serie: form.serie.trim().toUpperCase(),
    };

    try {
      await axios.post(`${API_BASE}/inventario`, dataEnviar);
      showToast("Equipo guardado correctamente", "success");
      limpiarFormulario();
      onSaved && onSaved();
    } catch (error) {
      console.error("❌ ERROR AL GUARDAR:", error);
      showToast("Ocurrió un error inesperado al guardar", "error");
    } finally {
      setIsSaving(false);
      setShowConfirm(false);
    }
  };

  // =========================
  // LISTAS
  // =========================
  const listaEquipos = [
    "Monitor",
    "Impresora",
    "CPU",
    "Escáner de Huellas",
    "Pad de Firma",
    "Terminal de Cobros",
    "Brazo Articulado",
    "Cámara Profesional",
    "Micrófono",
    "Escáner de Documentos",
    "Lector Óptico",
    "Otro",
  ];

  const listaMarcas = [
    "WACOM",
    "SUPREMA INC",
    "XEROX",
    "SWIFTPRO",
    "CANON",
    "HP",
    "IDP",
    "NETPAY",
    "STEREN",
    "THALES",
    "OTRA",
  ];

  // =========================
  // RENDER
  // =========================
  return (
    <div className="w-full min-h-screen p-10 bg-gray-50 overflow-y-auto">
      <div className="w-full max-w-7xl mx-auto bg-white p-10 rounded-2xl shadow-xl border border-gray-200">
        
        <h2 className="text-4xl font-bold mb-10 text-slate-800 tracking-tight border-b pb-3">
          Agregar Equipo al Inventario
        </h2>

        <form onSubmit={handleSubmit} className="space-y-14">

          {/* ==================== INFORMACIÓN DEL EQUIPO ==================== */}
          <section>
            <h3 className="text-2xl font-semibold mb-6 text-slate-700">
              Información del Equipo
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">

              {/* ID */}
              <div>
                <label className="font-medium text-slate-700">ID *</label>
                <input
                  disabled
                  value={nuevoID}
                  className="w-full mt-2 p-3 border rounded-lg bg-gray-200 text-gray-700 font-semibold"
                />
              </div>

              {/* EQUIPO */}
              <div>
                <label className="font-medium text-slate-700">Equipo *</label>
                <select
                  name="equipo"
                  value={form.equipo}
                  onChange={handleChange}
                  className="w-full mt-2 p-3 border rounded-lg"
                >
                  <option value="">Seleccione un equipo</option>
                  {listaEquipos.map((eq) => (
                    <option key={eq} value={eq}>
                      {eq}
                    </option>
                  ))}
                </select>

                {form.equipo === "Otro" && (
                  <input
                    type="text"
                    placeholder="Especifique el equipo"
                    className="w-full mt-3 p-3 border rounded-lg border-blue-400"
                    value={equipoOtro}
                    onChange={(e) => setEquipoOtro(e.target.value)}
                  />
                )}
              </div>

              {/* MARCA */}
              <div>
                <label className="font-medium text-slate-700">Marca *</label>
                <select
                  name="marca"
                  value={form.marca}
                  onChange={handleChange}
                  className="w-full mt-2 p-3 border rounded-lg"
                >
                  <option value="">Seleccione una marca</option>
                  {listaMarcas.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>

                {form.marca === "OTRA" && (
                  <input
                    type="text"
                    placeholder="Especifique la marca"
                    className="w-full mt-3 p-3 border rounded-lg border-blue-400"
                    value={marcaOtra}
                    onChange={(e) => setMarcaOtra(e.target.value)}
                  />
                )}
              </div>

              {/* MODELO */}
              <div>
                <label className="font-medium text-slate-700">Modelo *</label>
                <input
                  name="modelo"
                  value={form.modelo}
                  onChange={handleChange}
                  className="w-full mt-2 p-3 border rounded-lg"
                />
              </div>

              {/* SERIE */}
              <div>
                <label className="font-medium text-slate-700">Serie</label>
                <input
                  name="serie"
                  placeholder="Ejemplo: ABC12345"
                  value={form.serie}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      serie: e.target.value.toUpperCase().trim(),
                    })
                  }
                  className={`w-full mt-2 p-3 border rounded-lg ${
                    serieDuplicada ? "border-red-500 bg-red-50" : ""
                  }`}
                />

                {serieDuplicada && (
                  <p className="text-red-600 text-sm mt-2 font-semibold">
                    ❌ Esta serie ya existe en el inventario.
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* ==================== INFORMACIÓN OPERATIVA ==================== */}
          <section>
            <h3 className="text-2xl font-semibold mb-6 text-slate-700">
              Información Operativa
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <div>
                <label className="font-medium text-slate-700">Estado *</label>
                <select
                  name="estadoEquipo"
                  value={form.estadoEquipo}
                  onChange={handleChange}
                  className="w-full mt-2 p-3 border rounded-lg"
                >
                  <option value="">Seleccione el estado</option>
                  <option value="BUENO">BUENO</option>
                  <option value="REGULAR">REGULAR</option>
                  <option value="MALO">MALO</option>
                  <option value="EN REPARACIÓN">EN REPARACIÓN</option>
                  <option value="USO PERSONAL">USO PERSONAL</option>
                  <option value="NO OPERATIVO">NO OPERATIVO</option>
                </select>
              </div>

              <div>
                <label className="font-medium text-slate-700">
                  Fecha de Adquisición
                </label>
                <input
                  type="date"
                  name="fechaAdquisicion"
                  value={form.fechaAdquisicion}
                  onChange={handleChange}
                  className="w-full mt-2 p-3 border rounded-lg"
                />
              </div>

              <div>
                <label className="font-medium text-slate-700">Proyecto *</label>
                <select
                  name="proyecto"
                  value={form.proyecto}
                  onChange={handleChange}
                  className="w-full mt-2 p-3 border rounded-lg"
                >
                  <option value="">Selecciona un proyecto</option>
                  <option value="SYSNOT">SYSNOT</option>
                  <option value="TARJETA DE CIRCULACION">
                    TARJETA DE CIRCULACION
                  </option>
                  <option value="LICENCIAS">LICENCIAS DE CONDUCIR</option>
                  <option value="PASAPORTES OME CHETUMAL">
                    PASAPORTES OME CHETUMAL
                  </option>
                </select>
              </div>

              <div>
                <label className="font-medium text-slate-700">Módulo *</label>
                <input
                  name="modulo"
                  value={form.modulo}
                  onChange={handleChange}
                  className="w-full mt-2 p-3 border rounded-lg"
                />
              </div>
            </div>
          </section>

          {/* ==================== UBICACIÓN ==================== */}
          <section>
            <h3 className="text-2xl font-semibold mb-6 text-slate-700">
              Ubicación del Equipo
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <div>
                <label className="font-medium text-slate-700">Municipio *</label>
                <select
                  name="municipio"
                  value={form.municipio}
                  onChange={(e) => {
                    const mun = e.target.value;
                    const oficinas = OFICINAS_POR_MUNICIPIO[mun] || [];
                    setOficinasDisponibles(oficinas);
                    setForm((prev) => ({
                      ...prev,
                      municipio: mun,
                      oficina: oficinas.length ? oficinas[0] : "",
                    }));
                  }}
                  className="w-full mt-2 p-3 border rounded-lg"
                >
                  <option value="">Seleccione un municipio</option>
                  {Object.keys(OFICINAS_POR_MUNICIPIO).map((mun) => (
                    <option key={mun} value={mun}>
                      {mun}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-medium text-slate-700">Oficina *</label>
                <select
                  name="oficina"
                  value={form.oficina}
                  onChange={handleChange}
                  className="w-full mt-2 p-3 border rounded-lg"
                >
                  <option value="">Seleccione una oficina</option>
                  {oficinasDisponibles.map((of) => (
                    <option key={of} value={of}>
                      {of}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* ==================== NOTAS ==================== */}
          <section>
            <label className="font-medium text-lg text-slate-700">Notas</label>
            <textarea
              name="notas"
              value={form.notas}
              onChange={handleChange}
              className="w-full p-4 border rounded-lg h-36 mt-3"
            />
          </section>

          {/* ==================== BOTÓN ==================== */}
          <div className="pt-6">
            <button
              type="submit"
              disabled={isSaving || serieDuplicada}
              className={`px-14 py-4 text-xl rounded-xl shadow-lg transition text-white
                ${
                  serieDuplicada
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
            >
              {serieDuplicada
                ? "Serie duplicada"
                : isSaving
                ? "Guardando..."
                : "Guardar Equipo"}
            </button>
          </div>
        </form>

        {/* TOASTS */}
        <div className="toast-container">
          {toasts.map((t) => (
            <div key={t.id} className={`toast toast-${t.type}`}>
              <span>{t.message}</span>
              <span className="toast-close" onClick={() => cerrarToast(t.id)}>
                ×
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ==================== MODAL CONFIRMACIÓN ==================== */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[99998]">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-slate-200">
            
            <h3 className="text-xl font-semibold text-slate-800 mb-2">
              Confirmar guardado
            </h3>

            <p className="text-sm text-slate-600 mb-6">
              ¿Confirmas que deseas guardar este equipo en el inventario?
            </p>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={ejecutarGuardado}
                disabled={isSaving}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
              >
                {isSaving ? "Guardando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================
   ESTILOS PREMIUM PARA TOASTS
================================ */
const style = `
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
}

.toast {
  min-width: 300px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  box-shadow: 0 8px 25px rgba(0,0,0,0.20);
  animation: slideIn 0.45s ease-out;
}

.toast-success { background-color: #16a34a; }
.toast-error { background-color: #dc2626; }
.toast-warning { background-color: #f59e0b; }
.toast-info { background-color: #2563eb; }

.toast-close {
  margin-left: auto;
  cursor: pointer;
  font-size: 22px;
  opacity: 0.8;
}
.toast-close:hover {
  opacity: 1;
}
`;

document.head.insertAdjacentHTML("beforeend", `<style>${style}</style>`);
